<template>
  <b-container fluid>
    <backlink />
    <h1>Préparations en cours</h1>

    <AdminTable :config="config" :options="options" :items_provider="itemsProvider" ref="table">
      <template slot="renting" slot-scope="{data}">
        <router-link v-if="$store.getters['auth/isAuthorized']('renting_show')"
          :to="{ name: 'RentingDetail', params: { id: data.item.renting_id } }" target="_blank">{{
            data.item.renting.renting_number
          }}</router-link>
        <template v-else>{{ data.item.renting.renting_number }}</template>
        <b-badge v-if="data.item.renting.renting_type_slug == 'exchange'" variant="info" class="ml-1">Échange</b-badge>
      </template>
      <template slot="preparation_date" slot-scope="{data}">
        <span :class="$moment(data.value).isBefore($moment()) ? 'text-red' : null">{{ data.value | moment("DD-MM-YYYY")
        }}</span>
      </template>
      <template slot="preparator" slot-scope="{data}">
        {{ data.item.preparator.firstname }} {{ data.item.preparator.lastname }}
      </template>
      <template slot="group_slug" slot-scope="{ data }">
        <div style="width: 75px">
          <b-badge v-if="data.value.includes('outillage')" variant="outillage" class="circle float-left" v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('outillage').text">
            <i class="fa fa-check"></i>
          </b-badge>
          <b-badge v-if="data.value.includes('instruments')" variant="instruments" class="circle float-left"
            v-b-tooltip.hover :title="$store.getters['renting_preparation/getGroup']('instruments').text">
            <i class="fa fa-check"></i>
          </b-badge>
          <b-badge v-if="data.value.includes('service_et_logistique')" variant="service_et_logistique"
            class="circle float-left" v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('service_et_logistique').text">
            <i class="fa fa-check"></i>
          </b-badge>
        </div>
      </template>
      <template slot="stock" slot-scope="{data}">
        <StockByWarehousePopover
          :products="(data.item.renting_preparation_products || []).filter(rpp => rpp.product && rpp.product.id).map(rpp => ({ id: rpp.product.id, name: rpp.product.name }))">
          <b-badge v-if="data.value == 'on-stock'" variant="success" class="circle float-left" title="En stock">
            <i class="fa fa-check"></i>
          </b-badge>
          <b-badge v-else variant="danger" class="circle float-left" title="En rupture">
            <i class="fa fa-times"></i>
          </b-badge>
        </StockByWarehousePopover>
      </template>
      <template slot="actions" slot-scope="{ data }">
        <b-button @click.prevent="openSelectPreparatorModal(
          data.item
        )" v-b-modal.modal-cancel v-b-tooltip.hover title="Replanifier" variant="outline-secondary">
          <i class="far fa-calendar"></i>
        </b-button>
      </template>
    </AdminTable>

    <b-modal id="select_preparator_modal" lazy hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">
        <i class="far fa-toolbox"></i>
        Sélectionner un préparateur
      </template>
      <b-form>
        <!-- INC-001 : seuls les utilisateurs ayant accès à tous les entrepôts peuvent réaffecter -->
        <b-form-group v-if="isAllWarehouses" label="Entrepôt" label-for="warehouse_id">
          <b-form-select class="flex-fill" id="warehouse_id" v-model="selected_preparation.warehouse_id"
            :options="warehouse_options">
            <option slot="first" :value="null">-- Tous les entrepôts --</option>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Préparateur" label-for="preparator_id">
          <!-- Liste avec recherche : on tape le nom plutôt que de dérouler tous les préparateurs. -->
          <OptionsAutocomplete id="preparator_id" v-model="selected_preparation.preparator_id" variant="form" :limit="200"
            placeholder="Nom du préparateur…" :options="selected_preparation.group_slug == 'instruments'
              ? im_preparators
              : selected_preparation.group_slug == 'service_et_logistique'
                ? snl_preparators
                : ot_preparators
              " />
        </b-form-group>
        <b-form-group label="Date de préparation" label-for="preparation_date">
          <datepicker id="preparation_date" input-class="form-control" v-model="selected_preparation.preparation_date"
            required :format="'dd/MM/yyyy'" :monday-first="true" :language="fr" :clear-button="true"
            clear-button-icon="fa fa-times fa-lg" :disabledDates="disabled_preparation_dates">
          </datepicker>
        </b-form-group>
        <div class="form-submit">
          <b-button type="submit" variant="outline-primary"
            :disabled="!selected_preparation.preparator_id || !selected_preparation.preparation_date"
            @click.prevent="selectPreparator()">
            Enregistrer</b-button>
        </div>
      </b-form>
    </b-modal>

  </b-container>
</template>

<script>
import AdminTable from "@/components/Global/AdminTableSync";
import config from "./config";
import StockByWarehousePopover from "@/components/Global/StockByWarehousePopover";

import Datepicker from "vuejs-datepicker";
import { en, fr } from "vuejs-datepicker/dist/locale";

export default {
  name: "RentingPreparationList",
  components: {
    AdminTable,
    StockByWarehousePopover,
    Datepicker,
  },
  data() {
    return {
      en: en,
      fr: fr,
      selected_preparation: {
        preparator: null,
        preparation_date: null,
        group_slug: null,
      },
    };
  },
  computed: {
    // On planifie une préparation : la date ne peut pas être dans le passé. `to` désactive
    // tout ce qui est strictement antérieur, aujourd'hui reste donc sélectionnable.
    disabled_preparation_dates: function () {
      return { to: this.$moment().startOf("day").toDate() };
    },
    // INC-001 : true si l'utilisateur n'a pas d'entrepôt rattaché (accès à tous les entrepôts)
    isAllWarehouses: function () {
      return !this.$store.getters["auth/warehouse"];
    },
    // INC-001 : on retire le filtre entrepôt pour les utilisateurs rattachés à un
    // entrepôt (périmètre déjà forcé côté serveur)
    config: function () {
      if (this.isAllWarehouses) {
        return config;
      }
      return { ...config, fields: config.fields.filter((field) => field.name !== "warehouse") };
    },
    // INC-001 : entrepôts proposés pour la réaffectation
    warehouse_options: function () {
      return this.$store.getters["warehouse/getAll"].selectOptions("id", ["internal_code", " - ", "name"]) || [];
    },
    options: function () {
      return {
        tabs_options: [
          {value: 'past', text: "Passées"},
          {value: 'today', text: "Aujourd'hui"}
        ],
        // Le filtre liste les mêmes habilités que les sélecteurs d'affectation, tous groupes confondus.
        preparator_options: this.$store.getters["renting_preparator/getAll"].selectOptions("id", ["firstname", " ", "lastname"]),
        group_slug_options: this.$store.getters["renting_preparation/getGroups"],
        // INC-003 : options du filtre entrepôt ("-- Tout --" ajouté par AdminTableSync)
        warehouse_options: this.warehouse_options,
        // Échanges (SFD Échanges et stockage 1.1) : suffixe -E, badge « Échange ».
        type_options: [
          { value: null, text: "Tous" },
          { value: 'standard', text: "Standard" },
          { value: 'exchange', text: "Échange" },
        ],
        // Longue durée (SFD 2.8) : isole les préparations engagées, qui se traitent à part.
        long_term_filter_options: [
          { value: null, text: "Toutes" },
          { value: '1', text: "Avec engagement" },
          { value: '0', text: "Sans engagement" },
        ],
      };
    },
    // Les trois listes ne dépendent que du droit porté, jamais du rôle : un administrateur
    // habilité est proposé au même titre qu'un préparateur.
    im_preparators: function () {
      return (
        this.$store.getters["renting_preparator/getByAuthorization"]("renting_im-preparator")
          .selectOptions("id", ["firstname", " ", "lastname"]) || []
      );
    },
    ot_preparators: function () {
      return (
        this.$store.getters["renting_preparator/getByAuthorization"]("renting_ot-preparator")
          .selectOptions("id", ["firstname", " ", "lastname"]) || []
      );
    },
    snl_preparators: function () {
      return (
        this.$store.getters["renting_preparator/getByAuthorization"]("renting_snl-preparator")
          .selectOptions("id", ["firstname", " ", "lastname"]) || []
      );
    },
  },
  mounted() {
    // Même source que la modale d'affectation du détail commande : cette liste porte
    // authorization_list, que /user/index/select ne renvoyait pas — les trois sélecteurs
    // restaient donc vides.
    this.$store.dispatch("renting_preparator/getAll")
    // INC-001 : entrepôts pour la réaffectation (seulement utile en accès-tout)
    if (this.isAllWarehouses) {
      this.$store.dispatch("warehouse/getAll");
    }
  },
  methods: {
    /**
     * Fourni les données à AdminTableSync. Cette méthode est appelée à chaque 
     * rafraichissement de la liste
     * @param   {object}  ctx   Objet context envoyé par b-table
     * @returns {object}        {
                                  total: Nombre total de résultats,
                                  items: Items à afficher
                                }
     */
    itemsProvider(ctx) {
      var self = this;
      return new Promise(function (resolve, reject) {
        self.$http
          .get(process.env.BACK_API_URL + "rentingpreparation", {
            params: {
              page: ctx.currentPage,
              limit: ctx.perPage,
              sortBy: ctx.sortBy,
              sortDesc: ctx.sortDesc ? "DESC" : "ASC",
              search: ctx.filter,
              preparationDate1: ctx.extended_filters_model.tabs && ctx.extended_filters_model.tabs == 'today'
                ? self
                    .$moment()
                    .format("YYYY-MM-DD")
                : ((!ctx.extended_filters_model.tabs || ctx.extended_filters_model.tabs == null) && ctx.extended_filters_model.preparation_date.start ? self
                    .$moment(ctx.extended_filters_model.preparation_date.start)
                    .format("YYYY-MM-DD") : null),
              preparationDate2: ctx.extended_filters_model.tabs ? (
                ctx.extended_filters_model.tabs == 'past'
                ? self
                    .$moment()
                    .subtract(1, 'days')
                    .format("YYYY-MM-DD")
                : (
                  ctx.extended_filters_model.tabs == 'today'
                  ? self
                      .$moment()
                      .format("YYYY-MM-DD")
                  : null
                )
              ) : (ctx.extended_filters_model.preparation_date.end ? self
                    .$moment(ctx.extended_filters_model.preparation_date.end)
                    .format("YYYY-MM-DD") : null),
              preparator: ctx.extended_filters_model.preparator,
              groupSlug: ctx.extended_filters_model.group_slug,
              statusSlug: ['renting-preparation-en-attente-de-preparation', 'renting-preparation-en-preparation'],
              // INC-003 : filtre entrepôt de la page (ignoré côté serveur si l'user est déjà scopé)
              warehouse: ctx.extended_filters_model.warehouse,
              type: ctx.extended_filters_model.type,
              // Longue durée (SFD 2.8) : préparations portant au moins une ligne engagée.
              long_term: ctx.extended_filters_model.long_term_filter,
            },
          })
          .then((response) => {
            resolve({
              total: response.data.total,
              page: parseInt(response.data.page),
              items: response.data.rentingPreparations || [],
            });
          })
          .catch((error) => {
            self.$toastr.error("Le chargement de la liste a échoué");
            self.inGenerating = false;
          });
      });
    },
    openSelectPreparatorModal(renting_preparation) {
      this.selected_preparation = {
        ...renting_preparation,
        preparator_id: renting_preparation.preparator ? renting_preparation.preparator.id : null,
        // INC-001 : entrepôt courant de la préparation (réaffectation par les users accès-tout)
        warehouse_id: renting_preparation.warehouse ? renting_preparation.warehouse.id : null,
        planning: true
      }
      this.$root.$emit("bv::show::modal", "select_preparator_modal");

    },
    selectPreparator() {
      this.$store.dispatch(config.storeNamespace+"/saveItem", this.selected_preparation).then(() => {
        this.$root.$emit("bv::hide::modal", "select_preparator_modal");
        this.$refs.table.refresh()
      });
    },
  },
};
</script>


<style lang="scss">
@import "~@/assets/scss/_variables.scss";

.text-red {
  color: $red;
}
</style>


// WEBPACK FOOTER //
// src/components/RentingPreparation/RentingPreparationList.vue